import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import type { FacilitiesTableRowData } from '../facilitiesTableOptions';
import type {
  DateRangeFilter,
  FacilitiesFilterState,
  NumericRangeFilter,
  StringFilter,
} from '~/core/routes/credit-facilities/PortfolioFacilitiesProvider/facilitiesFilterTypes';

export const filterByStringRange = (
  value: string | null,
  stringEntryFilter?: StringFilter,
): boolean => {
  if (!stringEntryFilter) return true;
  if (!value) return false;

  const id = stringEntryFilter.options.find((option) => option.name === value)
    ?.id;
  finleyAssert(
    id,
    `[Value: ${value}] does not exist in options for multi-select dropdown`,
  );

  return stringEntryFilter.selectedOptions.includes(id);
};

export const filterByNumericRange = (
  value: number | null,
  range?: NumericRangeFilter,
): boolean => {
  if (!range || (range.min === null && range.max === null)) return true;
  if (value === null) return false;

  if (typeof range.min === 'number' && value < range.min) return false;
  if (typeof range.max === 'number' && value > range.max) return false;

  return true;
};

export const filterByDateRange = (
  value: Util.FullDateStr | null | undefined,
  range?: DateRangeFilter,
): boolean => {
  if (!range || (range.startDate === null && range.endDate === null))
    return true;
  if (value === null || value === undefined) return false;

  if (range.startDate && value < range.startDate) return false;
  if (range.endDate && value > range.endDate) return false;

  return true;
};

export const filterFacilitiesTableRows = (
  allComputedRows: FacilitiesTableRowData[],
  filters: FacilitiesFilterState,
): FacilitiesTableRowData[] => {
  if (!filters || Object.keys(filters).length === 0) return allComputedRows;

  return allComputedRows.filter(
    (row) =>
      filterByStringRange(
        row.creditFacilityName,
        filters.staticFilters.facilityIds,
      ) &&
      filterByDateRange(row.maturityDate, filters.staticFilters.maturityDate) &&
      filterByDateRange(row.closingDate, filters.staticFilters.closingDate) &&
      filterByDateRange(
        row.dateOfLastFundingRequest,
        filters.staticFilters.dateOfLastFundingRequest,
      ) &&
      filterByNumericRange(
        row.globalCommitmentAmount,
        filters.staticFilters.globalCommitmentAmount,
      ) &&
      filterByNumericRange(
        row.globalLoanOutstanding,
        filters.staticFilters.globalLoanOutstanding,
      ) &&
      filterByNumericRange(
        row.globalUnusedCommitment,
        filters.staticFilters.globalUnusedCommitment,
      ) &&
      filterByNumericRange(
        row.globalUtilization,
        filters.staticFilters.globalUtilization,
      ) &&
      filterByNumericRange(
        row.collectionsAccountBalance,
        filters.staticFilters.collectionsAccountBalance,
      ) &&
      filterByNumericRange(
        row.requiredReserve,
        filters.staticFilters.requiredReserve,
      ) &&
      filterByNumericRange(
        row.totalBorrowingBase,
        filters.staticFilters.totalBorrowingBase,
      ) &&
      filterByNumericRange(
        row.borrowingBaseFromReceivables,
        filters.staticFilters.borrowingBaseFromReceivables,
      ) &&
      filterByNumericRange(
        row.borrowingBaseAvailability,
        filters.staticFilters.borrowingBaseAvailability,
      ) &&
      filterByNumericRange(
        row.accruedInterestAndFees,
        filters.staticFilters.accruedInterestAndFees,
      ) &&
      filterByNumericRange(
        row.capitalProviderCommitmentAmount,
        filters.staticFilters.capitalProviderCommitmentAmount,
      ) &&
      filterByNumericRange(
        row.capitalProviderLoanOutstanding,
        filters.staticFilters.capitalProviderLoanOutstanding,
      ) &&
      filterByNumericRange(
        row.capitalProviderUnused,
        filters.staticFilters.capitalProviderUnused,
      ) &&
      filterByNumericRange(
        row.capitalProviderUtilization,
        filters.staticFilters.capitalProviderUtilization,
      ),
  );
};
